import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useGmbDescriptionGuide, useSaveGmbDescription, useSuggestGmbDescription } from '../hooks/useGmbStrategy';

interface GuideCheck {
  id: string;
  label: string;
  status: 'good' | 'needs_attention' | 'unavailable';
  message: string;
  matched?: string[];
  missing?: string[];
  comparisons?: {
    label: string;
    platform_value?: string | null;
    google_value?: string | null;
    status: 'consistent' | 'mismatch' | 'unavailable';
  }[];
}

interface DescriptionGuideData {
  description: string;
  character_count: number;
  max_characters: number;
  can_save: boolean;
  checks: GuideCheck[];
  keyword_suggestions: string[];
  service_suggestions: string[];
  suggestions: string[];
  near_me_opportunities?: { phrase: string; subject: string; in_description: boolean }[];
}

const checkStyles = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300',
  needs_attention: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300',
  unavailable: 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

export default function DescriptionKeywordGuide() {
  const { data, isLoading, error, refetch } = useGmbDescriptionGuide();
  const saveMutation = useSaveGmbDescription();
  const suggestMutation = useSuggestGmbDescription();
  const guide = data as DescriptionGuideData | undefined;
  const [draft, setDraft] = useState('');
  const [suggestion, setSuggestion] = useState('');

  useEffect(() => {
    if (guide) setDraft(guide.description || '');
  }, [guide?.description]);

  if (isLoading) {
    return <div className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">Analyzing description and local keyword coverage...</div>;
  }

  if (error || !guide) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-5 dark:border-red-900 dark:bg-red-950/20">
        <p className="text-sm text-red-700 dark:text-red-300">Could not load the Description & Keyword Improvement Guide.</p>
        <button onClick={() => refetch()} className="mt-3 text-sm font-semibold text-red-700 underline dark:text-red-300">Retry</button>
      </div>
    );
  }

  const changed = draft.trim() !== guide.description;
  const tooLong = draft.trim().length > guide.max_characters;
  const canSave = !!draft.trim() && !tooLong && changed && !saveMutation.isPending;
  const statusIcon = (status: GuideCheck['status']) =>
    status === 'good' ? 'ri-checkbox-circle-line' : status === 'needs_attention' ? 'ri-error-warning-line' : 'ri-information-line';

  const handleSuggest = async () => {
    try {
      const result = await suggestMutation.mutateAsync();
      const text = result?.suggested_description || result?.data?.suggested_description;
      if (!text) throw new Error('No suggestion was returned.');
      setSuggestion(text);
    } catch (suggestError: any) {
      toast.error(suggestError?.message || 'Could not generate a suggested description');
    }
  };

  const nearMe = guide.near_me_opportunities || [];

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync(draft.trim());
      toast.success('Google Business Profile description updated');
    } catch (saveError: any) {
      toast.error(saveError?.message || 'Could not update the Google Business Profile description');
    }
  };

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Description & Keyword Improvement Guide</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Review description quality, local phrase coverage, and platform-to-Google NAP consistency.</p>
        </div>
        <span className={`text-xs font-semibold ${tooLong ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {draft.trim().length}/{guide.max_characters} characters
        </span>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {guide.checks.map((check) => (
          <div key={check.id} className={`rounded-lg border p-3 ${checkStyles[check.status]}`}>
            <div className="flex items-center gap-2 text-xs font-semibold">
              <i className={`${statusIcon(check.status)} text-base`}></i>
              {check.label}
            </div>
            <p className="mt-1 text-xs leading-relaxed">{check.message}</p>
            {check.missing && check.missing.length > 0 && (
              <p className="mt-2 text-[11px]">Missing: {check.missing.join(', ')}</p>
            )}
            {check.comparisons?.filter((item) => item.status !== 'unavailable').map((item) => (
              <p key={item.label} className="mt-2 text-[11px]">
                {item.label}: {item.status === 'consistent' ? 'matches' : 'differs'}
                {item.status === 'mismatch' && ` (platform: ${item.platform_value}; Google: ${item.google_value})`}
              </p>
            ))}
          </div>
        ))}
      </div>

      {(guide.suggestions.length > 0 || guide.keyword_suggestions.length > 0 || guide.service_suggestions.length > 0) && (
        <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-4 dark:border-sky-900 dark:bg-sky-950/20">
          <h4 className="text-xs font-semibold text-sky-900 dark:text-sky-200">Improvement suggestions</h4>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-sky-900 dark:text-sky-200">
            {guide.suggestions.map((suggestion) => <li key={suggestion}>{suggestion}</li>)}
          </ul>
          {(guide.keyword_suggestions.length > 0 || guide.service_suggestions.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {[...guide.keyword_suggestions, ...guide.service_suggestions].map((phrase) => (
                <button
                  key={phrase}
                  type="button"
                  onClick={() => setDraft((current) => `${current.trim()}${current.trim() ? ' ' : ''}${phrase}`)}
                  className="rounded-full border border-sky-300 bg-white px-2.5 py-1 text-[11px] font-medium text-sky-800 hover:bg-sky-100 dark:border-sky-800 dark:bg-gray-900 dark:text-sky-200 dark:hover:bg-sky-900/40"
                  title="Append phrase to the description draft"
                >
                  + {phrase}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {nearMe.length > 0 && (
        <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 p-4 dark:border-violet-900 dark:bg-violet-950/20">
          <h4 className="text-xs font-semibold text-violet-900 dark:text-violet-200">"Near me" keyword opportunities</h4>
          <p className="mt-1 text-[11px] text-violet-800 dark:text-violet-300">
            Local searches you should appear for. Weave them naturally into the description, post copy and product/service listings.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {nearMe.map((item) => (
              <span
                key={item.phrase}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                  item.in_description
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                    : 'border-violet-300 bg-white text-violet-800 dark:border-violet-800 dark:bg-gray-900 dark:text-violet-200'
                }`}
                title={item.in_description ? 'Already in your description' : 'Not in your description yet'}
              >
                {item.in_description && <i className="ri-check-line"></i>}
                {item.phrase}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 rounded-lg border border-teal-200 bg-teal-50 p-4 dark:border-teal-900 dark:bg-teal-950/20">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h4 className="text-xs font-semibold text-teal-900 dark:text-teal-200">Suggested improved description</h4>
            <p className="mt-1 text-[11px] text-teal-800 dark:text-teal-300">
              AI rewrite using your services, location and local phrases, within {guide.max_characters} characters. Review it before saving.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSuggest}
            disabled={suggestMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {suggestMutation.isPending ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>}
            {suggestMutation.isPending ? 'Writing suggestion...' : suggestion ? 'Suggest again' : 'Suggest improved description'}
          </button>
        </div>
        {suggestion && (
          <>
            <p className="mt-3 whitespace-pre-line rounded-md border border-teal-200 bg-white p-3 text-xs leading-relaxed text-gray-800 dark:border-teal-900 dark:bg-gray-900 dark:text-gray-200">
              {suggestion}
            </p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-teal-800 dark:text-teal-300">{suggestion.length}/{guide.max_characters} characters</span>
              <button
                type="button"
                onClick={() => setDraft(suggestion)}
                className="text-xs font-semibold text-teal-800 underline hover:text-teal-900 dark:text-teal-300"
              >
                Use this in the editor below
              </button>
            </div>
          </>
        )}
      </div>

      <label htmlFor="gmb-description-draft" className="mt-4 block text-xs font-semibold text-gray-700 dark:text-gray-300">
        Edit Google Business Profile description
      </label>
      <textarea
        id="gmb-description-draft"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        maxLength={guide.max_characters + 100}
        rows={5}
        className={`mt-2 w-full resize-y rounded-lg border px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 dark:bg-gray-900 dark:text-white ${tooLong ? 'border-red-400 focus:ring-red-200 dark:border-red-700' : 'border-gray-300 focus:ring-sky-200 dark:border-gray-600 dark:focus:ring-sky-900'}`}
      />
      {tooLong && <p className="mt-1 text-xs text-red-600 dark:text-red-400">Google Business Profile descriptions cannot exceed {guide.max_characters} characters.</p>}
      {saveMutation.isError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{saveMutation.error.message}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">Save updates the connected Google listing and the saved GMB snapshot.</p>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="inline-flex items-center gap-2 rounded-lg bg-sky-700 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveMutation.isPending ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          {saveMutation.isPending ? 'Saving to Google...' : 'Save description'}
        </button>
      </div>
    </section>
  );
}